import React, {
  ReactElement,
  ReactNode,
  type HTMLInputTypeAttribute,
  type InputHTMLAttributes,
  type JSX,
} from 'react';

import {
  Box,
  MenuItem,
  Stack,
  Switch,
  Checkbox,
  type BaseTextFieldProps,
  type BoxProps,
  type FormControlLabelProps,
  type TextFieldProps,
  type SwitchProps,
  type SelectProps,
  type CheckboxProps,
} from '@mui/material';
import { TimePicker, TimePickerProps } from '@mui/x-date-pickers-pro';

import {
  UseControllerProps,
  useController,
  FieldValues,
  UseFormGetFieldState,
  Path,
} from 'react-hook-form';

import ErrorMessage from 'libs/react-hook-form/ErrorMessage';
import type {
  S3SignedRetrieveApiArg,
  S3SignedUploadUrl,
} from 'services/api/ailyssaApi';
import {
  CustomDropdownField,
  CustomFormControlLabel,
  CustomTextField,
} from './CustomFormControls';
import FormFieldDescription from './FormFieldDescription';
import FieldSet from './FieldSet';

interface FieldRendererProps<T extends FieldValues>
  extends UseControllerProps<T> {
  label?: string | ReactNode;
  subtitle?: string;
}

interface SwitchFieldProps<T extends FieldValues>
  extends Omit<FieldRendererProps<T>, 'label'> {
  validationRequiredFields?: string[];
  getFieldState?: UseFormGetFieldState<FieldValues>;
  switchProps?: SwitchProps;
  label: FormControlLabelProps['label'];
}

type DropdownItemType = {
  label: string | number;
  value: string | number;
};

interface DropdownFieldRendererProps<T extends FieldValues>
  extends Omit<FieldRendererProps<T>, 'type'> {
  items: DropdownItemType[];
  multiple?: boolean;
  required?: boolean;
  selectProps?: SelectProps;
}

export interface FileUploadFieldRendererProps<T extends FieldValues>
  extends Omit<FieldRendererProps<T>, 'type'> {
  context: S3SignedRetrieveApiArg['context'];
  onChange?: (resp: S3SignedUploadUrl | string, file?: File) => void;
  required?: boolean;
  defaultImage?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  triggerBtn?: JSX.Element;
  displaySnackbarOnSuccess?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  // [field]_url returned by backend when the form is populated
  uploadedImageUrlName?: Path<T> | null;
}

export interface TextFieldRendererProps<T extends FieldValues>
  extends FieldRendererProps<T> {
  type: HTMLInputTypeAttribute;
  inputProps?: BaseTextFieldProps['inputProps'];
  containerStyles?: BoxProps['sx'];
  required?: boolean;
  disableTextField?: boolean;
  textFieldProps?: TextFieldProps;
  fieldAction?: ReactElement;
}

type CustomFormControlLabelProps = Omit<
  FormControlLabelProps,
  'label' | 'title' | 'control'
>;

const TextFieldRenderer = <T extends FieldValues>({
  type,
  label,
  subtitle,
  inputProps,
  containerStyles,
  required,
  textFieldProps = {},
  disableTextField = false,
  fieldAction,
  ...props
}: TextFieldRendererProps<T>) => {
  const {
    field: { onChange, value, ...otherFieldProps },
    formState: { errors },
  } = useController({
    ...props,
  });

  const { disabled } = props;

  return (
    <Box sx={{ display: 'grid', height: '100%', ...containerStyles }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box>
          <FormFieldDescription
            required={required}
            title={label}
            subtitle={subtitle}
          />
        </Box>
        <Box>{fieldAction}</Box>
      </Stack>
      <FieldSet viewOnly={disableTextField}>
        <CustomTextField
          style={{
            width: '100%',
            backgroundColor: disabled ? 'lightgrey' : 'inherit',
          }}
          size="small"
          type={type}
          inputProps={inputProps}
          {...otherFieldProps}
          value={value ?? ''}
          onChange={({ target: { value: inputValue } }) => {
            if (type === 'number' && inputValue !== '')
              onChange(Number(inputValue));
            else onChange(inputValue);
          }}
          {...textFieldProps}
        />
      </FieldSet>
      <ErrorMessage errors={errors} name={props.name} />
    </Box>
  );
};

const TimeFieldRenderer = <T extends FieldValues>({
  label,
  subtitle,
  required,
  timePickerProps,
  ...props
}: Omit<FieldRendererProps<T>, 'type'> & {
  required?: boolean;
  timePickerProps?: TimePickerProps<Date>;
}) => {
  const {
    field: { ref, value, onChange },
    formState: { errors },
  } = useController(props);

  return (
    <Box sx={{ display: 'grid', height: '100%' }}>
      <Box>
        <FormFieldDescription
          required={required}
          title={label}
          subtitle={subtitle}
        />
      </Box>
      <TimePicker
        ref={ref}
        value={value && new Date(`2012-10-10T${value}`)}
        ampm={false}
        onChange={(date) => {
          const time = !date ? '' : date.toTimeString().substring(0, 8);
          onChange(time);
        }}
        {...timePickerProps}
      />
      <ErrorMessage errors={errors} name={props.name} />
    </Box>
  );
};

const SwitchFieldRenderer = <T extends FieldValues>({
  label,
  subtitle,
  formControlLabelProps = {},
  getFieldState,
  switchProps,
  ...props
}: Omit<SwitchFieldProps<T>, 'type'> & {
  formControlLabelProps?: CustomFormControlLabelProps;
}) => {
  const { field } = useController(props);

  return (
    <CustomFormControlLabel
      control={
        <Switch
          size="small"
          checked={field.value || false}
          {...field}
          {...switchProps}
          id={switchProps?.name}
        />
      }
      label={label || ''}
      title={subtitle}
      {...formControlLabelProps}
    />
  );
};

const SelectFieldRenderer = <T extends FieldValues>({
  label,
  items,
  subtitle,
  multiple,
  required,
  selectProps,
  ...props
}: DropdownFieldRendererProps<T>) => {
  const {
    field: { value: currValue, ...others },
    formState: { errors },
  } = useController(props);

  let adjustedCurrValue: string | number | undefined | (string | number)[] =
    currValue;

  if (!currValue && currValue !== 0) {
    adjustedCurrValue = multiple ? [] : '';
  }

  return (
    <Stack>
      <FormFieldDescription
        required={required}
        title={label}
        subtitle={subtitle}
      />
      <CustomDropdownField
        size="small"
        value={adjustedCurrValue}
        {...others}
        multiple={multiple}
        {...selectProps}
      >
        {items.map(({ label: itemLabel, value }) => (
          <MenuItem key={value} value={value}>
            {itemLabel}
          </MenuItem>
        ))}
      </CustomDropdownField>
      <ErrorMessage errors={errors} name={props.name} />
    </Stack>
  );
};

interface CheckboxFieldRendereProps<T extends FieldValues>
  extends FieldRendererProps<T> {
  checkboxProps?: CheckboxProps;
  formControlLabelProps?: CustomFormControlLabelProps;
  label: string;
}

const CheckboxFieldRenderer = <T extends FieldValues>({
  checkboxProps,
  formControlLabelProps,
  label,
  ...props
}: CheckboxFieldRendereProps<T>) => {
  const { field } = useController(props);

  return (
    <CustomFormControlLabel
      control={
        <Checkbox
          {...checkboxProps}
          {...field}
          checked={field.value || false}
        />
      }
      label={label}
      {...formControlLabelProps}
    />
  );
};

export {
  TextFieldRenderer,
  SwitchFieldRenderer,
  SelectFieldRenderer,
  TimeFieldRenderer,
  CheckboxFieldRenderer,
};
